import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ACTION_STATUSES } from 'shared/consts';
import { LocaleEnum } from 'store/cart/types';
import { OwnerTypesEnum } from 'store/app-instance-configs/enums';
import { getAppInstanceConfigs } from 'store/app-instance-configs/actions';
import { getTransaction, getTransactions } from 'store/transactions/actions';
import {
  selectPastTransactions,
  selectTransaction,
  selectTransactionStatus,
  selectTransactions,
  selectTransactionsStatus,
} from 'store/transactions/selectors';
import { TransactionDetails } from 'store/transactions/types';
import { useLocale } from 'hooks/use-locale.hook';

interface ResolveTransactionArgs {
  /** The transaction uuid, when the caller has one. Resolves a booking of ANY status. */
  transactionUuid?: string | null;
  /** `details.id` -- the booking id. Resolves an UPCOMING booking only. See the seam below. */
  bookingId?: number | string | null;
}

interface ResolveTransactionResult {
  transaction: TransactionDetails | undefined;
  isLoading: boolean;
  isNotFound: boolean;
}

/**
 * Whether `status` has been observed settling since `key` last changed.
 *
 * Both transaction statuses are shared, app-wide "last fetch" values: either can already read
 * FULFILLED (or REJECTED) from an earlier, unrelated fetch by the time a given identifier becomes
 * current. Trusting that immediately reports a not-found -- and bounces the reader off the page --
 * before this hook's own request has run. So a settled status only counts once PENDING has been
 * seen for the CURRENT identifier first. REJECTED settles too, so a failed fetch cannot hang.
 */
const useSettledSince = (status: ACTION_STATUSES | undefined, key: string): boolean => {
  const hasSeenPendingRef = useRef(false);
  const [isSettled, setIsSettled] = useState(false);

  useEffect(() => {
    hasSeenPendingRef.current = false;
    setIsSettled(false);
  }, [key]);

  useEffect(() => {
    if (status === ACTION_STATUSES.PENDING) {
      hasSeenPendingRef.current = true;
    } else if (
      hasSeenPendingRef.current &&
      (status === ACTION_STATUSES.FULFILLED || status === ACTION_STATUSES.REJECTED)
    ) {
      setIsSettled(true);
    }
  }, [status]);

  return isSettled;
};

/**
 * One door for "which booking is this?", whichever identifier the caller happens to hold.
 *
 * Links into this app arrive carrying one of two things. Most carry the TRANSACTION uuid; some --
 * older minted deeplinks, and anything built from a row that only ever had the booking id -- carry
 * `details.id`. They are not interchangeable, and which one you get decides what you can reach:
 *
 * - `transactionUuid` fetches a single record (`/transactions/:uuid`), which is NOT upcoming-scoped
 *   and so resolves a booking of any status, past or cancelled included.
 * - `bookingId` has no single-record endpoint of its own on this client. `getTransactions` takes no
 *   parameters at all -- `is_upcoming=true` is fixed in the url -- so an id can only be matched
 *   against that list, and a booking that has already happened is not in it.
 *
 * THE SEAM: that second leg is where a past-booking-by-id fallback belongs, once an endpoint exists
 * to back it. It is one `find` with nothing underneath it, deliberately in one place rather than
 * copied into each caller, so adding the fallback is a change here and nowhere else.
 *
 * Both legs read the SINGLE-TRANSACTION SLOT before the list, and that ordering is load-bearing in
 * a way worth spelling out. `openReceipt` hands off to `/booking-details/:bookingId` -- an id, not
 * a uuid -- and clears only `currentTransaction` on its way; the slot `getTransaction` fills
 * survives (only `resetTransaction`, in `onCloseReceipt`, empties it). So a booking resolved by
 * uuid at the door is still in the slot when the detail page asks for it by id, and matching the
 * slot on `details.id` is what carries a past booking across that hop. Without it the detail page
 * falls back to the upcoming list and a past booking opens blank.
 *
 * The slot is shared with other flows (checkout, redirect), so every read of it is guarded on the
 * identifier actually being asked for -- a stale value left there by something else cannot leak
 * through as this caller's answer.
 */
export const useResolveTransaction = ({
  transactionUuid,
  bookingId,
}: ResolveTransactionArgs): ResolveTransactionResult => {
  const dispatch = useDispatch();
  const transaction = useSelector(selectTransaction);
  const transactions = useSelector(selectTransactions);
  const pastTransactions = useSelector(selectPastTransactions);
  const transactionStatus = useSelector(selectTransactionStatus);
  const transactionsStatus = useSelector(selectTransactionsStatus);
  const locale = useLocale();

  const hasUuid = Boolean(transactionUuid);
  // `''` is absent, not an id to look for: a url param that round-tripped empty must read as "no
  // request" rather than as a search that is still running.
  const hasBookingId = bookingId !== undefined && bookingId !== null && bookingId !== '';

  /*
   * The past list is searched as a fallback, which is what lets a booking that has already happened
   * resolve by id; the upcoming list still answers first. Resolving nothing is what used to drive the
   * render loop the ref guard in components/upcoming-bookings/components/bookings.tsx also covers. It
   * answers only for what has been fetched, so a cold deeplink to a past booking still falls through
   * to the uuid leg — the single-record fetch answers for any status.
   */
  const foundTransaction = useMemo(() => {
    if (hasUuid) {
      const fromSlot = transaction?.uuid === transactionUuid ? transaction : undefined;
      const matchesUuid = ({ uuid }: TransactionDetails) => uuid === transactionUuid;

      return fromSlot ?? transactions?.find(matchesUuid) ?? pastTransactions?.find(matchesUuid);
    }

    if (hasBookingId) {
      /*
       * Compared as strings on every leg: `details.id` is typed `string | number` and an id that
       * arrived through a url param is always a string, so `===` would silently miss whenever the
       * two sides represent the same id in different JS types (42 !== '42').
       */
      const fromSlot = transaction && String(transaction.details?.id) === String(bookingId) ? transaction : undefined;
      const matchesBookingId = ({ details }: TransactionDetails) => String(details?.id) === String(bookingId);

      return fromSlot ?? transactions?.find(matchesBookingId) ?? pastTransactions?.find(matchesBookingId);
    }

    return undefined;
  }, [hasUuid, hasBookingId, transaction, transactions, pastTransactions, transactionUuid, bookingId]);

  // One key for both gates, so changing EITHER identifier re-arms them together.
  const requestKey = `${transactionUuid ?? ''}|${bookingId ?? ''}`;
  const isListSettled = useSettledSince(transactionsStatus, requestKey);
  const isSingleSettled = useSettledSince(transactionStatus, requestKey);

  /*
   * The single fetch only counts towards settling when one was actually asked for. Folding it in
   * unconditionally would hang the id-only leg forever: it dispatches no single fetch, so that
   * status never goes PENDING for this key and the gate never closes.
   */
  const isSettled = hasUuid ? isSingleSettled && isListSettled : isListSettled;

  /*
   * ⚠️ THE SINGLE RECORD IS REFRESHED. Asked for once per identifier, whether or not a copy of
   * that booking is already in hand.
   *
   * A booking opened a second time would otherwise show whatever was last said about it, for as
   * long as the copy survives -- a check-in that has since happened, an add-on that has since been
   * paid for, a cancellation made on another device. `/transactions/:uuid` is one record and the
   * only identifier this client has an endpoint for, so this is the cheapest honest answer
   * available; it is NOT the whole-page list fetch the guard below exists to prevent.
   *
   * ⛔ AND IT DOES NOT BLINK -- a property of `isLoading`, not of this effect. Both `isLoading` and
   * `isNotFound` are gated on `!foundTransaction`, so a refresh running underneath a booking that
   * is already on screen changes nothing the reader can see until the new answer lands.
   *
   * Keyed on `requestKey` rather than re-run freely, because the arrival it causes puts a new
   * object in the slot -- which would ask again, and again.
   */
  const refreshedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hasUuid || refreshedKeyRef.current === requestKey) {
      return;
    }

    refreshedKeyRef.current = requestKey;
    // Resolves any status. The list below backs the fallback underneath it.
    dispatch(getTransaction.request(transactionUuid));
  }, [dispatch, hasUuid, requestKey, transactionUuid]);

  /*
   * ⛔ THE LIST IS RESOLVED, NOT REFRESHED. It is not asked for while the identifier is already
   * answered.
   *
   * RES-1367: this used to fire on every change of identifier, answered or not, which the panes
   * turned into a fetch of the whole upcoming list on every click in it. Both legs of this page
   * land here -- the restore reads the selection the click just wrote, and the booking column
   * remounts under a new key -- so one click asked for the list twice. The epic's switchMap
   * collapsed them into one call, but both had already set `loading`, and the list blinked out
   * into skeletons underneath the reader's cursor.
   *
   * What makes the guard safe is that `isLoading` and `isNotFound` below are themselves gated on
   * `!foundTransaction`: with an answer in hand neither of the settle gates is ever consulted, so
   * skipping the request cannot leave one of them waiting on a PENDING that never comes.
   *
   * ⚠️ `foundTransaction` is a DEPENDENCY on purpose, and the early return is what makes that
   * cheap. It is a new object every time the list is replaced, so this effect re-runs on each
   * arrival -- and asks for nothing, because by then it has what it came for.
   *
   * ⚠️ And this is not the list's refresh policy, which is a separate question with a separate
   * answer (useUpcomingBookings, which fetches once and does not re-ask either). It was only ever
   * refreshing as a side effect of resolving, which is not a thing to rely on.
   */
  useEffect(() => {
    if (foundTransaction) {
      return;
    }

    if (hasUuid || hasBookingId) {
      dispatch(getTransactions.request({}));
    }
  }, [dispatch, transactionUuid, bookingId, hasUuid, hasBookingId, foundTransaction]);

  const ownerUuid = foundTransaction?.details?.owner?.owner_uuid;
  const ownerType = foundTransaction?.details?.owner?.owner_type;

  useEffect(() => {
    // Both, not just the uuid: the type says an owner carries a type, but this is an API response
    // and `strict` is off here, so an owner with a uuid and no type reaches `.toUpperCase()` and
    // throws inside the effect. This resolver now runs on every cold restore, not just a click.
    if (ownerUuid && ownerType) {
      dispatch(
        getAppInstanceConfigs.request({
          lang: locale as LocaleEnum,
          owners: [
            {
              uuid: ownerUuid,
              type: ownerType.toUpperCase() as OwnerTypesEnum,
            },
          ],
        }),
      );
    }
  }, [dispatch, ownerUuid, ownerType, locale]);

  const hasRequest = hasUuid || hasBookingId;

  return {
    transaction: foundTransaction,
    // With no identifier there is nothing to fetch or wait on, so this reads as a settled "no
    // result" rather than loading forever.
    isLoading: hasRequest && !foundTransaction && !isSettled,
    isNotFound: hasRequest && !foundTransaction && isSettled,
  };
};
